/**
 * Click Outside Hook
 *
 * Detects clicks outside a referenced element and triggers a callback.
 * Commonly used for closing dropdowns, modals, and menus.
 *
 * @example
 * // Basic usage
 * const dropdownRef = useRef<HTMLDivElement>(null)
 * useClickOutside(dropdownRef, () => setIsOpen(false))
 *
 * @example
 * // Conditional (only when dropdown is open)
 * const dropdownRef = useRef<HTMLDivElement>(null)
 * useClickOutside(dropdownRef, () => setIsOpen(false), isOpen)
 *
 * @example
 * // Multiple refs
 * const ref1 = useRef(null)
 * const ref2 = useRef(null)
 * useClickOutside([ref1, ref2], () => setIsOpen(false))
 */

import { useEffect, RefObject } from 'react'

/**
 * Detect clicks outside one or more elements
 *
 * @param ref - A ref or array of refs to monitor
 * @param handler - Callback function to execute when clicking outside
 * @param enabled - Whether the hook is active (default: true)
 */
export function useClickOutside(
  ref: RefObject<HTMLElement | null> | RefObject<HTMLElement | null>[],
  handler: (event: MouseEvent) => void,
  enabled: boolean = true,
) {
  useEffect(() => {
    if (!enabled) return

    const handleClickOutside = (event: MouseEvent) => {
      // Support single ref or array of refs
      const refs = Array.isArray(ref) ? ref : [ref]

      // Check if click is outside ALL refs.
      // Null/unmounted refs are skipped (treated as "not containing the click")
      // so they don't prevent the handler from firing.
      const clickedOutside = refs.every(
        r => !r.current || !r.current.contains(event.target as Node),
      )

      if (clickedOutside) {
        handler(event)
      }
    }

    // Use mousedown instead of click for better UX
    // (allows selecting text without triggering close)
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [ref, handler, enabled])
}
